import BookingWhatsApp from "@/components/BookingWhatsApp";
import { CONTACT, BRAND } from "@/lib/constants";
import { MapPin, Mail, Phone, Clock } from "lucide-react";
import usePageTitle from "@/lib/usePageTitle";

export default function ContactPage() {
  usePageTitle("Contact");
  return (
    <>
      <section
        data-testid="contact-hero"
        className="bg-white border-b border-[#E2E8F0]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
                Contact · WhatsApp Booking
              </div>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#0A2540]">
                Reach the clinic
                <br />
                <span className="italic text-[#2E5AA5]">in one tap.</span>
              </h1>
              <p className="mt-6 max-w-xl font-sans text-base text-[#475569] leading-relaxed">
                {BRAND.name} runs a single, low-friction booking channel:
                WhatsApp. For everything else — email, phone, or drop in during
                clinic hours.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-l border-[#E2E8F0]">
                <div
                  className="p-6 border-r border-b border-[#E2E8F0]"
                  data-testid="contact-card-address"
                >
                  <MapPin
                    size={16}
                    strokeWidth={1.5}
                    className="text-[#B08A4A] mb-3"
                  />
                  <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8] mb-1">
                    Clinic
                  </div>
                  <div className="font-sans text-sm text-[#0A2540] leading-relaxed">
                    {CONTACT.address.line1}
                    <br />
                    {CONTACT.address.line2}
                    <br />
                    {CONTACT.address.city}
                  </div>
                </div>
                <div
                  className="p-6 border-r border-b border-[#E2E8F0]"
                  data-testid="contact-card-email"
                >
                  <Mail
                    size={16}
                    strokeWidth={1.5}
                    className="text-[#B08A4A] mb-3"
                  />
                  <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8] mb-1">
                    Email
                  </div>
                  <a
                    href={`mailto:${CONTACT.email}`}
                    className="font-sans text-sm text-[#0A2540] hover:text-[#2E5AA5] break-all"
                  >
                    {CONTACT.email}
                  </a>
                </div>
                <div
                  className="p-6 border-r border-b border-[#E2E8F0]"
                  data-testid="contact-card-phone"
                >
                  <Phone
                    size={16}
                    strokeWidth={1.5}
                    className="text-[#B08A4A] mb-3"
                  />
                  <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8] mb-1">
                    Phone / WhatsApp
                  </div>
                  <a
                    href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                    className="font-sans text-sm text-[#0A2540] hover:text-[#2E5AA5]"
                  >
                    {CONTACT.phone}
                  </a>
                </div>
                <div
                  className="p-6 border-r border-b border-[#E2E8F0]"
                  data-testid="contact-card-hours"
                >
                  <Clock
                    size={16}
                    strokeWidth={1.5}
                    className="text-[#B08A4A] mb-3"
                  />
                  <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8] mb-1">
                    Hours
                  </div>
                  <ul className="font-sans text-xs text-[#0A2540] space-y-0.5">
                    {CONTACT.hours.map((h) => (
                      <li key={h.label} className="flex justify-between gap-3">
                        <span>{h.label}</span>
                        <span className="text-[#475569]">{h.time}</span>
                      </li>
                    ))}
                  </ul>
                  <div
                    data-testid="contact-appointment-note"
                    className="mt-3 inline-block border border-[#B08A4A]/40 bg-[#B08A4A]/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A]"
                  >
                    {CONTACT.appointmentNote}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BookingWhatsApp heading={false} />
    </>
  );
}
